import React, { useEffect, useMemo, useState } from "react";
import TourOverlay from "./TourOverlay";
import { obterAlvo } from "./registro";
import {
  carregarVistos, consumirRever, decidir, jaViu, marcarVisto, marcarConcluido,
  matarPreview, pediuRever, previewAtivo,
} from "./storage";
import { bloqueadoPor } from "@/utils/filaDeModais";

/**
 * Tour — o decisor. Montado DENTRO da tela: decide SE o tour roda
 * (papel certo, primeira visita, momento certo) e com QUAIS passos.
 *
 *  - `pronto` espera os DADOS que montam os alvos: o filtro descarta passo
 *    cujo alvo não está no DOM — decidir cedo demais descartaria passos de
 *    cards ainda carregando E marcaria tudo como visto.
 *  - `adiar` = tarefa crítica em andamento (corrida ativa, checkout): não
 *    mostra e NÃO marca — tenta na próxima visita.
 *  - marca "visto" AO MOSTRAR (ver storage.js).
 *  - "já viu" vem do SERVIDOR, por usuário: espera a resposta antes de decidir.
 */
export default function TourDaTela({ tela, roteiro, papeis, pronto, adiar = false }) {
  const [ativo, setAtivo] = useState(null); // null=decidindo · {passos, preview}
  const usuario = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; }
  }, []);

  // undefined = ainda perguntando · null = servidor não respondeu (conta como visto)
  const [servidor, setServidor] = useState(undefined);
  useEffect(() => {
    let vivo = true;
    carregarVistos(usuario?.id).then((telas) => { if (vivo) setServidor(telas); });
    return () => { vivo = false; };
  }, [usuario]);

  // O tour e' o ULTIMO da fila de modais do boot (utils/filaDeModais). Sem isto
  // o balao subia por cima do popup de novidades e o overlay de um roubava o
  // clique do outro — a tela ficava travada, sem responder a nada.
  //
  // Comeca esperando e so' consulta depois de CARENCIA: os modais acima abrem
  // com atraso proprio (o de novidades, 800ms). Checar no mount encontraria a
  // fila vazia e o tour passaria na frente de quem ainda ia abrir.
  const [esperando, setEsperando] = useState(true);
  useEffect(() => {
    if (ativo !== null) return undefined;      // ja' decidiu: para de consultar
    const CARENCIA = 1500;
    let intervalo = null;
    const consultar = () => setEsperando(Boolean(bloqueadoPor("tour")));
    const inicio = setTimeout(() => {
      consultar();
      intervalo = setInterval(consultar, 600);
    }, CARENCIA);
    return () => {
      clearTimeout(inicio);
      if (intervalo) clearInterval(intervalo);
    };
  }, [ativo]);

  useEffect(() => {
    if (ativo !== null || !pronto || servidor === undefined) return;
    const decisao = decidir({
      preview: previewAtivo(),
      papel: usuario?.role,
      papeis,
      pronto,
      // `adiar` do chamador OU modal de maior prioridade aberto
      adiar: adiar || esperando,
      // "Rever tutoriais" da Ajuda roda mais uma vez, mesmo já visto
      visto: !pediuRever(tela) && jaViu(tela, usuario?.id, servidor),
    });
    if (decisao === "nao") return;
    // passos cujo alvo não existe são descartados; alvo vazio = balão central
    const passos = roteiro.filter((p) => !p.alvo || obterAlvo(p.alvo));
    if (!passos.length) return;
    if (decisao === "mostrar") {
      consumirRever(tela);
      marcarVisto(tela, usuario?.id); // AO MOSTRAR
    }
    setAtivo({ passos, preview: decisao === "preview" });
  }, [ativo, pronto, adiar, esperando, servidor, papeis, roteiro, tela, usuario]);

  if (!ativo) return null;

  const encerrar = (concluiu) => {
    if (ativo.preview) matarPreview();           // fechar MATA o preview
    else if (concluiu) marcarConcluido(tela, usuario?.id);
    setAtivo(false);
  };
  return (
    <TourOverlay
      passos={ativo.passos}
      onFechar={() => encerrar(false)}
      onConcluir={() => encerrar(true)}
    />
  );
}
